import {Ionicons} from '@expo/vector-icons';
import {PlatformPressable, useHeaderHeight} from '@react-navigation/elements';
import {useLinkBuilder, useNavigation} from '@react-navigation/native';
import type {
  StackNavigationProp,
  StackScreenProps,
} from '@react-navigation/stack';
import dayjs from 'dayjs';
import {observer} from 'mobx-react-lite';
import {useCallback, useLayoutEffect, useMemo} from 'react';
import {Platform, StyleSheet} from 'react-native';

import {CarouselScrollView} from '../../components/CarouselScrollView';
import {createNullableContext, useForcedContext} from '../../context';
import {PRODUCTION_WEB_PREFIX} from '../../LocationSchema';
import {expr} from '../../mobx-toolbox';
import {
  useNavigationGetIsFocused,
  useStackNavigationState,
} from '../../Navigation';
import useNavigationRoute from '../../Navigation/useNavigationRoute';
import {useRoot} from '../../Root';
import type {RootStackParamList} from '../../RootStack/RootStackParamList';
import ShowInsightScreen from '../../screens/ShowInsightScreen';
import {useStyles} from '../../styling';
import type {Day, Millisecond} from '../../Time';
import useDailyContent from './useDailyContent';
import useDailySlides from './useDailySlides';

export type ShowDailyBindingProps = StackScreenProps<
  RootStackParamList,
  'ShowDaily'
>;

export default observer(function ShowDailyBinding(
  props: ShowDailyBindingProps,
) {
  const {navigation, route} = props;
  return (
    <ShowDailyStableBinding navigation={navigation} routeKey={route.key} />
  );
});

type ShowDailyStableBindingProps = Pick<ShowDailyBindingProps, 'navigation'> & {
  routeKey: string;
};

const ShowDailyStableBinding = observer(function ShowDailyStableBinding(
  props: ShowDailyStableBindingProps,
) {
  const {navigation, routeKey} = props;

  const provider = useStackNavigationState(navigation);
  const getIsFocused = useNavigationGetIsFocused(routeKey, provider);

  const getRoute = useNavigationRoute<RootStackParamList, 'ShowDaily'>(
    routeKey,
    provider,
  );

  const getShift = useCallback(
    () =>
      expr(() => {
        const _shift = getRoute()?.params?.shift;
        return (_shift ? stubNan(parseInt(_shift, 10)) : 0) as Day;
      }),
    [getRoute],
  );

  const root = useRoot();
  const now = expr(
    () => root.currentTime.wrapped.startOf('day').valueOf() as Millisecond,
  );
  const setShift = useCallback(
    (_: Day) => {
      navigation.setParams({shift: `${_}`});
    },
    [navigation],
  );

  return (
    <ShowDailyCarouselBinding
      getShift={getShift}
      setShift={setShift}
      now={now}
      getIsFocused={getIsFocused}
    />
  );
});

type ShowDailyCarouselBindingProps = {
  getShift: () => Day;
  setShift: (_: Day) => void;
  getIsFocused: () => boolean;
  now: Millisecond;
};

const ShowDailyCarouselBinding = observer(function ShowDailyCarouselBinding(
  props: ShowDailyCarouselBindingProps,
) {
  const {getShift, setShift, getIsFocused, now} = props;

  const [slides, getIndex, setIndex] = useDailySlides(now, getShift, setShift);

  const styles = useStyles(theme => ({
    root: {
      flex: 0,
      flexGrow: 1,
      flexShrink: 0,
      backgroundColor: theme.palette.background,
    },
  }));

  const commonDailyProps = useMemo(
    () => ({
      getIsFocused,
      getShift,
    }),
    [getIsFocused, getShift],
  );

  return (
    <CommonDailyPropsContext.Provider value={commonDailyProps}>
      <CarouselScrollView
        style={styles.root}
        slides={slides}
        SlideComponent={ShowSingleDailyBinding}
        getIndex={getIndex}
        setIndex={setIndex}
        getIsFocused={getIsFocused}
      />
    </CommonDailyPropsContext.Provider>
  );
});

type ShowSingleDailyBindingProps = {
  shift: Day;
  now: Millisecond;
};

const ShowSingleDailyBinding = observer(function ShowSingleDailyBinding(
  props: ShowSingleDailyBindingProps,
) {
  const {now, shift} = props;

  const today = dayjs(now).format('D MMMM YYYY').toLowerCase();

  const headerHeight = useHeaderHeight();

  const {getIsFocused, getShift} = useForcedContext(CommonDailyPropsContext);

  const {quoteId, quoteBody, quoteSource, topicId, topicBody} = useDailyContent(
    now,
    {
      period: (getShift() !== shift ? 0 : 5000) as Millisecond,
      skip: !getIsFocused(),
    },
  );

  const root = useRoot();
  const {canShare} = root.locatorSharingState;

  const navigation =
    useNavigation<StackNavigationProp<RootStackParamList, 'ShowDaily'>>();
  const buildLink = useLinkBuilder();

  const onShareOrCopyPress = useCallback(async () => {
    const path = buildLink('ShowInsight', {
      quoteId: quoteId ?? -1,
      topicId: topicId ?? -1,
    });
    if (!path) {
      return;
    }
    await root.locatorSharingState.share(
      `${Platform.OS === 'web' ? location.origin : PRODUCTION_WEB_PREFIX}${path}`,
      {
        title: 'Поделиться озарением...',
      },
    );
  }, [buildLink, quoteId, root, topicId]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: _props => {
        const {pressColor, pressOpacity, tintColor} = _props;
        return (
          <PlatformPressable
            onPress={onShareOrCopyPress}
            pressColor={pressColor}
            pressOpacity={pressOpacity}>
            <Ionicons
              name={canShare ? 'share-outline' : 'copy-outline'}
              size={24}
              color={tintColor}
              style={layoutStyles.headerRightIcon}
            />
          </PlatformPressable>
        );
      },
    });
  }, [canShare, navigation, onShareOrCopyPress, quoteId, topicId]);

  return (
    <ShowInsightScreen
      today={today}
      compensateHeaderHeight={headerHeight}
      quoteBody={wrap(quoteBody)}
      quoteSource={wrap(quoteSource)}
      topicBody={wrap(topicBody)}
    />
  );
});

const layoutStyles = StyleSheet.create({
  headerRightIcon: {
    marginHorizontal: 24,
  },
});

function wrap(text: string | null | undefined) {
  if (typeof text === 'string') {
    return text;
  } else if (text === undefined) {
    return 'Загрузка...';
  }
  return 'Странно, тут ничего нет.';
}

function stubNan(_: number): number {
  if (Number.isNaN(_)) {
    return 0;
  }
  return _;
}

type CommonDailyProps = {
  getIsFocused: () => boolean;
  getShift: () => Day;
};

const CommonDailyPropsContext = createNullableContext<CommonDailyProps>();
